import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Typography,
  LinearProgress,
  Grid,
  Paper,
  Alert,
} from '@mui/material';
import { api, Question, GameSession } from '../services/api';
import { useLanguage } from '../translations/LanguageContext';

const GamePage: React.FC = () => {
  const navigate = useNavigate();
  const { translations } = useLanguage();
  const [question, setQuestion] = useState<Question | null>(null);
  const [gameSession, setGameSession] = useState<GameSession | null>(null);
  const [timeLeft, setTimeLeft] = useState(60);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [usedLifelines, setUsedLifelines] = useState<string[]>([]);
  const [removedOptionIds, setRemovedOptionIds] = useState<string[]>([]);
  const [hint, setHint] = useState<string | null>(null);
  const initialized = useRef(false);

  const handleGameEnd = useCallback(async () => {
    if (gameSession) {
      try {
        const finalSession = await api.endGame(gameSession.id);
        navigate('/game-end', { 
          state: { 
            score: gameSession.current_score,
            time: formatTime(finalSession.total_time),
            gameSessionId: gameSession.id
          } 
        });
      } catch (err) {
        console.error('Failed to end game session:', err);
      }
    }
  }, [gameSession, navigate]);

  const fetchQuestion = useCallback(async (sessionId: string) => {
    try {
      setLoading(true);
      setError(null);
      const newQuestion = await api.getQuestion(sessionId);
      const updatedSession = await api.getGameSession(sessionId);
      setGameSession(updatedSession);
      setQuestion(newQuestion);
      setTimeLeft(60);
      setRemovedOptionIds([]);
      setHint(null);
      setUsedLifelines([]);
    } catch (err: any) {
      if (err.response?.data?.error === 'No more questions available') {
        handleGameEnd();
      } else {
        setError('Failed to fetch question');
      }
    } finally {
      setLoading(false);
    }
  }, [handleGameEnd]);

  const initializeGame = useCallback(async () => {
    try {
      const session = await api.startGame();
      setGameSession(session);
      await fetchQuestion(session.id);
    } catch (err) {
      setError('Failed to initialize game');
      setLoading(false);
    }
  }, [fetchQuestion]);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    initializeGame();
  }, [initializeGame]);

  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      handleGameEnd();
    }
  }, [timeLeft, handleGameEnd]);

  const handleAnswer = async (optionId: string) => {
    if (!question || !gameSession) return;

    try {
      setLoading(true);
      const result = await api.submitAnswer(
        question.id,
        optionId,
        gameSession.id,
        usedLifelines
      );

      if (result.correct) {
        const updatedSession = await api.getGameSession(gameSession.id);
        setGameSession(updatedSession);
        await fetchQuestion(gameSession.id);
      } else {
        handleGameEnd();
      }
    } catch (err) {
      setError('Failed to submit answer');
      setLoading(false);
    }
  };

  const use5050 = async () => {
    if (!question || !gameSession || usedLifelines.includes('50/50')) return;

    try {
      const result = await api.use5050(question.id, gameSession.id);
      setRemovedOptionIds(result.removed_option_ids);
      setUsedLifelines([...usedLifelines, '50/50']);
    } catch (err) {
      setError('Failed to use 50/50 lifeline');
    }
  };

  const useTip = async () => {
    if (!question || !gameSession || usedLifelines.includes('tip')) return;

    try {
      const result = await api.useTip(question.id, gameSession.id);
      setHint(result.hint);
      setUsedLifelines([...usedLifelines, 'tip']);
    } catch (err) {
      setError('Failed to use tip lifeline');
    }
  };

  const formatTime = (ms: number): string => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    return `${minutes}:${(seconds % 60).toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <Container>
        <Box sx={{ width: '100%', mt: 4 }}>
          <LinearProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 4,
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">{translations.common.score}: {gameSession?.current_score || 0}</Typography>
            <Typography variant="h6">
              {translations.game.timeLeft}: {timeLeft} {translations.game.seconds}
            </Typography>
          </Box>
          
          <LinearProgress 
            variant="determinate" 
            value={(timeLeft / 60) * 100} 
            sx={{ mb: 4 }}
          />

          {question && (
            <>
              <Paper elevation={3} sx={{ p: 3, mb: 4, bgcolor: 'background.paper' }}>
                <Typography variant="h5" gutterBottom>
                  {question.text}
                </Typography>
                <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                  {translations.game.difficulty}: {translations.difficulty[question.difficulty as keyof typeof translations.difficulty]}
                </Typography>
              </Paper>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 4 }}>
                <Typography variant="h6">
                  {translations.game.lifelines}:
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button 
                    variant="contained" 
                    onClick={use5050}
                    disabled={usedLifelines.includes('50/50')}
                  >
                    50/50
                  </Button>
                  <Button 
                    variant="contained" 
                    onClick={useTip}
                    disabled={usedLifelines.includes('tip')}
                  >
                    {translations.game.tip}
                  </Button>
                </Box>
              </Box>

              {hint && (
                <Alert severity="info" sx={{ mb: 4 }}>
                  {translations.game.hint}: {hint}
                </Alert>
              )}

              <Grid container spacing={2}>
                {question.options
                  .filter(option => !removedOptionIds.includes(option.id))
                  .map((option) => (
                    <Grid item xs={12} sm={6} key={option.id}>
                      <Button
                        variant="outlined"
                        fullWidth
                        size="large"
                        onClick={() => handleAnswer(option.id)}
                        sx={{ height: '80px', textTransform: 'none', bgcolor: 'background.paper' }}
                      >
                        {option.text}
                      </Button>
                    </Grid>
                  ))}
              </Grid>
            </>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default GamePage; 